import { useState } from "react";
import { Share2, X, Copy, Check, Send, User, MoreHorizontal } from "lucide-react";
import useShare from "../../hooks/useShare";
import useShareToDM from "../../hooks/useShareToDM";

const SHARE_COLOR = 'oklch(72.3% 0.219 149.579)';

const XTwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// ── Section 1: DM share (primary) ─────────────────────────────────────────────
function DMShareSection({ postId, onClose }) {
  const {
    filtered, query, setQuery,
    selected, toggleSelect,
    optionalText, setOptionalText,
    loading, sending, sentCount,
    handleSend,
  } = useShareToDM(postId, onClose);

  return (
    <div className="mb-1">
      <p className="text-white/55 text-xs font-semibold uppercase tracking-wider mb-3">
        Send to friends
      </p>

      {/* Search */}
      <div className="relative mb-3">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search people..."
          className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl
            px-4 py-2.5 text-sm text-white placeholder-white/25
            focus:outline-none focus:border-white/20 transition-colors"
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X className="w-3.5 h-3.5 text-white/30" />
          </button>
        )}
      </div>

      {/* Contacts row */}
      {loading ? (
        <div className="flex gap-3 py-1 overflow-x-auto">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="shrink-0 flex flex-col items-center gap-1.5">
              <div className="w-12 h-12 rounded-full bg-white/[0.07] animate-pulse" />
              <div className="w-10 h-1.5 rounded bg-white/[0.07] animate-pulse" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-white/25 text-sm text-center py-4">
          {query ? 'No matching contacts' : 'Follow someone to share with them'}
        </p>
      ) : (
        <div
          className="flex gap-3 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {filtered.slice(0, 20).map(contact => {
            const isSel = selected.includes(contact.user_id);
            return (
              <button
                key={contact.user_id}
                onClick={() => toggleSelect(contact.user_id)}
                className="shrink-0 flex flex-col items-center gap-1.5 focus:outline-none"
              >
                <div
                  className={`relative w-12 h-12 rounded-full overflow-hidden transition-all duration-200
                    ${isSel ? 'ring-2 ring-white scale-105' : 'ring-2 ring-transparent'}`}
                >
                  {contact.avatar ? (
                    <img
                      src={contact.avatar}
                      alt={contact.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-white/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-white/30" />
                    </div>
                  )}
                  {isSel && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <span className={`text-[10px] max-w-[48px] truncate transition-colors
                  ${isSel ? 'text-white' : 'text-white/40'}`}>
                  {contact.username}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Max recipients warning */}
      {selected.length >= 10 && (
        <p className="text-white/30 text-xs text-center mt-2">
          Max 10 recipients per share
        </p>
      )}

      {/* Optional text + send — only when someone is selected */}
      {selected.length > 0 && (
        <div className="mt-3 space-y-2">
          <input
            value={optionalText}
            onChange={e => setOptionalText(e.target.value)}
            placeholder="Add a message... (optional)"
            maxLength={200}
            className="w-full bg-white/[0.05] border border-white/[0.07] rounded-xl
              px-4 py-2.5 text-sm text-white placeholder-white/25
              focus:outline-none focus:border-white/15 transition-colors"
          />
          <button
            onClick={handleSend}
            disabled={sending || sentCount !== null}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold
              transition-all duration-200
              ${sentCount !== null
                ? 'bg-green-500/15 text-green-400 border border-green-500/20'
                : sending
                  ? 'bg-white/[0.08] text-white/40 cursor-not-allowed'
                  : 'bg-white text-black hover:bg-gray-100 active:scale-[0.98]'
              }`}
          >
            {sentCount !== null
              ? `Sent to ${sentCount} ${sentCount === 1 ? 'person' : 'people'} ✓`
              : sending
                ? 'Sending…'
                : `Send to ${selected.length} ${selected.length === 1 ? 'person' : 'people'}`
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const ShareButton = ({ postId, caption }) => {
  const {
    open, copied, shareCount, postUrl,
    openShare, closeModal, handleCopy, handlePlatformShare, handleMoreApps,
  } = useShare(postId);
  const [hovered, setHovered] = useState(false);

  const shareText = caption
    ? `Check this out on PicProfile — ${caption.slice(0, 40)}${caption.length > 40 ? '...' : ''}`
    : 'Check this out on PicProfile';

  const platforms = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: <Send className="w-4 h-4" />,
      getUrl: () => `https://wa.me/?text=${encodeURIComponent(`${shareText} ${postUrl}`)}`,
    },
    {
      id: 'twitter',
      label: 'Twitter / X',
      icon: <XTwitterIcon />,
      getUrl: () =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`,
    },
  ];

  return (
    <>
      {/* Trigger */}
      <button
        onClick={() => openShare(caption)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="flex items-center gap-1.5 p-2 rounded-full
          transition-all duration-200 hover:scale-110 active:scale-95"
      >
        <Share2
          className="w-6 h-6 transition-colors duration-200"
          style={{
            color: (open || hovered) ? SHARE_COLOR : 'rgb(243,244,246)',
            filter: open ? `drop-shadow(0 0 6px ${SHARE_COLOR})` : 'none',
          }}
        />
        {shareCount > 0 && (
          <span
            className="text-sm font-medium transition-colors duration-200"
            style={{ color: (open || hovered) ? SHARE_COLOR : 'rgb(209,213,219)' }}
          >{shareCount}</span>
        )}
      </button>

      {/* Bottom sheet */}
      {open && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70]
            flex items-end md:items-center md:justify-center"
          onClick={e => e.target === e.currentTarget && closeModal()}
        >
          <div className="bg-[#111] rounded-t-3xl md:rounded-2xl w-full md:max-w-[480px]
            border-t md:border border-white/10 shadow-2xl animate-slideUp md:animate-fadeIn
            max-h-[85vh] flex flex-col">

            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex justify-between items-center px-5 py-3 shrink-0">
              <h2 className="text-lg font-semibold text-white">Share</h2>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-full hover:bg-white/10 transition"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="px-5 pb-6 overflow-y-auto flex-1">

              {/* ── DM share (primary) ── */}
              <DMShareSection postId={postId} onClose={closeModal} />

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/[0.07]" />
                <span className="text-white/20 text-[11px] uppercase tracking-wider shrink-0">
                  Share elsewhere
                </span>
                <div className="flex-1 h-px bg-white/[0.07]" />
              </div>

              {/* ── External: copy link ── */}
              <div className="flex items-center gap-2 bg-white/5 border
                border-white/10 rounded-xl p-3 mb-3">
                <p className="flex-1 text-sm text-gray-400 truncate">{postUrl}</p>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                    text-sm font-medium transition-all duration-200 shrink-0
                    ${copied
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                >
                  {copied
                    ? <><Check className="w-4 h-4" /> Copied!</>
                    : <><Copy className="w-4 h-4" /> Copy</>
                  }
                </button>
              </div>

              {/* ── External: platforms ── */}
              <div className="flex flex-col gap-1">
                {platforms.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handlePlatformShare(p.id, p.getUrl())}
                    className="flex items-center gap-3 p-3 rounded-xl
                      hover:bg-white/10 transition-all duration-200 text-white/70 hover:text-white"
                  >
                    <span className="text-white/50">{p.icon}</span>
                    <span className="text-sm font-medium">{p.label}</span>
                  </button>
                ))}

                {/* More apps — triggers native share sheet */}
                <button
                  onClick={handleMoreApps}
                  className="flex items-center gap-3 p-3 rounded-xl
                    hover:bg-white/10 transition-all duration-200 text-white/70 hover:text-white"
                >
                  <span className="text-white/50"><MoreHorizontal className="w-4 h-4" /></span>
                  <span className="text-sm font-medium">More apps</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      <style>{`
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeInScale 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes fadeInScale {
          from { transform: scale(0.95); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ShareButton;
